/**
 * PlanetHexMesh — builds the hexsphere planet mesh for Three.js rendering.
 *
 * Replaces PlanetResourceBaker (deleted) and the management-panel use of
 * PlanetTerrainBaker (kept only for cloud-canvas baking and for the small
 * planet sprites in the PixiJS world view via planetTerrainClient). Same
 * noise seed produces the same field; we sample at 642 hex centroids
 * (Goldberg G(8)) and extrude each cell as a small prism.
 *
 * Output: single BufferGeometry with vertex colors and flat shading.
 *   - Terrain layer:  palette + biome + elevation band, stepped extrusion
 *   - Resource layer: signed-delta heatmap (mining + Phase 5 migration)
 *                     with optional scanner-zone reveal.
 *
 * Switch via setLayer('terrain' | 'resource'). Push live state via
 * setHexState(rows) / setScannerZones(zones). setRichnessDiffs is a legacy
 * adapter kept for old wires; new code should use setHexState.
 */

import * as THREE from 'three';
import { createNoise3D, type NoiseFunction3D } from 'simplex-noise';
import seedrandom from 'seedrandom';
import {
  getHexSphere,
  type HexCell,
  type Vec3,
  type HexFrequency,
  PLANET_BALANCE,
} from '@astra/shared';
import type { PlanetProfileWire, RichnessDiffWire, HexStateWire } from './state/planetPanelStore';

// ==================== TYPES ====================

export interface ScannerZoneWire {
  lat: number;
  lon: number;
  radiusKm: number;
}

export interface BuiltPlanetHexMesh {
  /** Three.js mesh ready to add to a scene (single draw call, flat shaded). */
  mesh: THREE.Mesh;
  /** Faces per triangle of the geometry → cell id (length = triangle count). */
  faceToCell: Uint16Array;
  /** Total number of cells (642 for G(8,0)). */
  cellCount: number;
  /** Per-cell elevation band (0 = ocean, 4 = peak). */
  cellBand: Uint8Array;
  /** Switch the visible layer. */
  setLayer(layer: 'terrain' | 'resource'): void;
  /** Update which scanner zones are providing high-res reveal. */
  setScannerZones(zones: ScannerZoneWire[]): void;
  /** Highlight a single hex with a gold outline; pass null to clear. */
  setSelectedHex(hexId: number | null): void;
  /** Update per-hex signed delta state. Triggers a resource-layer recolour. */
  setHexState(rows: HexStateWire[]): void;
  /**
   * Legacy per-km-cell depletion adapter. Maps (cell_lat, cell_lon) to
   * nearest hex centroid. Kept for backwards compatibility with the old
   * wire shape; new code should call setHexState directly.
   */
  setRichnessDiffs(diffs: RichnessDiffWire[]): void;
  /** Get a cell by id (for selection / info panel). */
  getCell(id: number): HexCell;
  /** Get the current per-cell organic/crystal effective richness (post depletion). */
  getEffectiveRichness(id: number): { organic: number; crystal: number };
  /** Free GPU and CPU resources. */
  dispose(): void;
}

// ==================== COORDINATE CONVERSION ====================

/**
 * Hex-sphere math uses (x, y, z) with z = sin(lat).
 * Three.js (and PlanetGlobe) uses (x, y, z) with y = sin(lat) and z = -cos(lat)·sin(lon).
 * Apply this once when emitting positions to the buffer.
 */
function hexToThree(v: Vec3): THREE.Vector3 {
  return new THREE.Vector3(v.x, v.z, -v.y);
}

// ==================== TERRAIN COLOR PALETTE ====================

interface Palette {
  ocean: [number, number, number];
  shore: [number, number, number];
  low:   [number, number, number];
  mid:   [number, number, number];
  high:  [number, number, number];
  peak:  [number, number, number];
}

const PALETTES: Record<string, Palette> = {
  terran: {
    ocean: [22, 44, 72],   shore: [34, 62, 88],
    low:   [52, 88, 58],   mid:   [68, 102, 62],
    high:  [98, 88, 68],   peak:  [170, 175, 180],
  },
  barren: {
    ocean: [85, 75, 65],   shore: [100, 90, 78],
    low:   [130, 118, 100], mid:  [148, 132, 110],
    high:  [118, 108, 95], peak: [160, 152, 140],
  },
  rocky: {
    ocean: [58, 52, 48],   shore: [75, 68, 62],
    low:   [95, 88, 80],   mid:   [110, 100, 88],
    high:  [88, 78, 72],   peak:  [140, 135, 128],
  },
  desert: {
    ocean: [90, 68, 40],   shore: [120, 90, 55],
    low:   [175, 145, 85], mid:   [190, 155, 90],
    high:  [145, 110, 70], peak:  [200, 180, 155],
  },
  ice: {
    ocean: [30, 50, 78],   shore: [55, 80, 108],
    low:   [140, 170, 190], mid:  [170, 195, 210],
    high:  [200, 215, 225], peak: [235, 240, 245],
  },
  volcanic: {
    ocean: [140, 45, 10],  shore: [90, 30, 12],
    low:   [45, 30, 25],   mid:   [60, 40, 30],
    high:  [50, 45, 42],   peak:  [110, 100, 90],
  },
  gas: {
    ocean: [140, 120, 90], shore: [160, 140, 100],
    low:   [175, 155, 110], mid:  [190, 170, 125],
    high:  [170, 150, 115], peak: [200, 185, 150],
  },
};

const ELEVATION_STOPS: Record<string, [number, number, number, number]> = {
  terran:   [0.32, 0.42, 0.55, 0.70],
  barren:   [0.05, 0.15, 0.45, 0.72],
  rocky:    [0.08, 0.18, 0.48, 0.70],
  desert:   [0.05, 0.12, 0.50, 0.75],
  ice:      [0.25, 0.35, 0.55, 0.72],
  volcanic: [0.20, 0.30, 0.50, 0.68],
  gas:      [0.30, 0.40, 0.55, 0.70],
};

const SEA_THRESHOLDS: Record<string, number> = {
  terran:   -0.20, barren:  0.70, rocky:    0.60, desert: 0.75,
  ice:       0.05, volcanic: 0.15, gas:      0.00,
};

const WARP_AMPS: Record<string, number> = {
  terran:   0.28, barren:  0.25, rocky:    0.30, desert: 0.20,
  ice:      0.35, volcanic: 0.35, gas:     0.00,
};

const BIOME_CORNERS = {
  coldDry: [120, 115, 100] as [number, number, number],
  coldWet: [35, 68, 45]    as [number, number, number],
  hotDry:  [170, 140, 80]  as [number, number, number],
  hotWet:  [30, 75, 38]    as [number, number, number],
};

const SNOW: [number, number, number] = [235, 240, 245];

function getPalette(t: string): Palette { return PALETTES[t] ?? PALETTES.barren; }
function getStops(t: string): [number, number, number, number] { return ELEVATION_STOPS[t] ?? ELEVATION_STOPS.barren; }

function clamp(v: number, lo: number, hi: number): number { return Math.max(lo, Math.min(hi, v)); }
function smoothstep(e0: number, e1: number, x: number): number {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
}
function lerp3(a: [number, number, number], b: [number, number, number], t: number): [number, number, number] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

function elevationToColour(elev: number, palette: Palette, stops: [number, number, number, number]): [number, number, number] {
  const t = (elev + 1) * 0.5;
  const [s0, s1, s2, s3] = stops;
  if (t < s0) return lerp3(palette.ocean, palette.shore, t / s0);
  if (t < s1) return lerp3(palette.shore, palette.low, (t - s0) / (s1 - s0));
  if (t < s2) return lerp3(palette.low, palette.mid, (t - s1) / (s2 - s1));
  if (t < s3) return lerp3(palette.mid, palette.high, (t - s2) / (s3 - s2));
  return lerp3(palette.high, palette.peak, Math.min(1, (t - s3) / (1 - s3)));
}

/** Quantize elevation [-1, 1] into 5 band indices [0..4]. */
function elevationBand(elev: number, stops: [number, number, number, number]): number {
  const t = (elev + 1) * 0.5;
  if (t < stops[0]) return 0;        // deep ocean
  if (t < stops[1]) return 1;        // shore
  if (t < stops[2]) return 2;        // land
  if (t < stops[3]) return 3;        // hill
  return 4;                          // peak
}

// Per-band radial offset above the unit sphere. Drives the stepped extrusion.
const BAND_HEIGHT: number[] = [0.000, 0.005, 0.013, 0.022, 0.033];

// ==================== NOISE BUNDLE ====================

interface PlanetNoise {
  // Terrain
  continent: NoiseFunction3D;
  biome: NoiseFunction3D;
  terrain: NoiseFunction3D;
  warpX: NoiseFunction3D;
  warpY: NoiseFunction3D;
  warpZ: NoiseFunction3D;
  temperature: NoiseFunction3D;
  humidity: NoiseFunction3D;
  // Resources (matches server PlanetField.ts exactly)
  organic: NoiseFunction3D;
  crystal: NoiseFunction3D;
  detail: NoiseFunction3D;
}

function buildNoise(profileSeed: number): PlanetNoise {
  return {
    continent:   createNoise3D(seedrandom(`terrain:continent:${profileSeed}`)),
    biome:       createNoise3D(seedrandom(`terrain:biome:${profileSeed}`)),
    terrain:     createNoise3D(seedrandom(`terrain:terrain:${profileSeed}`)),
    warpX:       createNoise3D(seedrandom(`terrain:warpX:${profileSeed}`)),
    warpY:       createNoise3D(seedrandom(`terrain:warpY:${profileSeed}`)),
    warpZ:       createNoise3D(seedrandom(`terrain:warpZ:${profileSeed}`)),
    temperature: createNoise3D(seedrandom(`terrain:temp:${profileSeed}`)),
    humidity:    createNoise3D(seedrandom(`terrain:humid:${profileSeed}`)),
    organic:     createNoise3D(seedrandom(`organic:${profileSeed}`)),
    crystal:     createNoise3D(seedrandom(`crystal:${profileSeed}`)),
    detail:      createNoise3D(seedrandom(`detail:${profileSeed}`)),
  };
}

/** Ridged 3-octave summed noise (simplified vs. baker for hex-scale resolution). */
function ridged(n: NoiseFunction3D, x: number, y: number, z: number, baseFreq: number): number {
  let sum = 0, freq = baseFreq, amp = 1, prev = 1;
  for (let i = 0; i < 3; i++) {
    let v = 1 - Math.abs(n(x * freq, y * freq, z * freq));
    v = v * v * prev;
    sum += v * amp;
    prev = clamp(v * 2, 0, 1);
    freq *= 2;
    amp *= 0.5;
  }
  return sum;
}

/** Elevation in [-1, 1]. Simplified version of PlanetTerrainBaker.sampleElevation. */
function sampleElevation(
  noise: PlanetNoise,
  v: Vec3,
  clusterDensity: number,
  planetType: string,
): number {
  const x = v.x, y = v.y, z = v.z;
  if (planetType === 'gas') {
    // Latitudinal bands with turbulence + storm spots.
    const lat = Math.asin(clamp(z, -1, 1));
    const bandFreq = 6 + clusterDensity * 4;
    const turbFreq = 3 + clusterDensity * 3;
    const turb = noise.continent(x * turbFreq, y * turbFreq, z * turbFreq) * 0.3;
    const warped = Math.sin((lat + turb) * bandFreq);
    const ewFreq = 4 + clusterDensity * 2;
    const ew = noise.biome(x * ewFreq, y * ewFreq, z * ewFreq) * 0.15;
    const stormFreq = 8 + clusterDensity * 6;
    const stormRaw = noise.terrain(x * stormFreq, y * stormFreq, z * stormFreq);
    const storm = Math.max(0, Math.abs(stormRaw) - 0.7) / 0.3;
    return clamp(warped * 0.5 + ew + storm * 0.3, -1, 1);
  }

  const stops = getStops(planetType);
  const shoreStop = stops[1];
  const contFreq = 0.9 + clusterDensity * 0.5;
  const warpAmp = WARP_AMPS[planetType] ?? 0;

  let cx = x, cy = y, cz = z;
  if (warpAmp > 0) {
    const wf = contFreq * 0.7;
    cx += noise.warpX(x * wf, y * wf, z * wf) * warpAmp;
    cy += noise.warpY(x * wf, y * wf, z * wf) * warpAmp;
    cz += noise.warpZ(x * wf, y * wf, z * wf) * warpAmp;
  }
  const continentRaw = noise.continent(cx * contFreq, cy * contFreq, cz * contFreq);

  const seaThresh = SEA_THRESHOLDS[planetType] ?? 0;
  const coastWidth = 0.08;
  const mask = smoothstep(seaThresh - coastWidth, seaThresh + coastWidth, continentRaw);
  const oceanDepthNorm = clamp((continentRaw + 1) / (seaThresh + 1), 0, 1);
  const oceanT = oceanDepthNorm * shoreStop;

  const biomeFreq = 2.5 + clusterDensity * 2.5;
  const biomeN = noise.biome(x * biomeFreq, y * biomeFreq, z * biomeFreq);
  const terrFreq = 6 + clusterDensity * 8;
  const ridgedNorm = ridged(noise.terrain, x, y, z, terrFreq) / 1.75;

  const aboveSea = clamp((continentRaw - seaThresh) / (1 - seaThresh), 0, 1);
  const detailFade = Math.sqrt(clamp(aboveSea / 0.3, 0, 1));
  const landFeatures = aboveSea * 0.40 + (biomeN * 0.15 + ridgedNorm * 0.45) * detailFade;
  const landT = shoreStop + clamp(landFeatures, 0, 1) * (1 - shoreStop);

  const t = oceanT * (1 - mask) + landT * mask;
  return clamp(t * 2 - 1, -1, 1);
}

/** Replicates server's sampleField (PlanetField.ts) exactly. */
function sampleResourceField(
  noise: PlanetNoise,
  v: Vec3,
  profile: PlanetProfileWire,
): { organic: number; crystal: number } {
  const baseFreq = 2 + profile.clusterDensity * 6;
  const detailFreq = baseFreq * 4;

  const orgLow = noise.organic(v.x * baseFreq, v.y * baseFreq, v.z * baseFreq);
  const orgHigh = noise.detail(v.x * detailFreq, v.y * detailFreq, v.z * detailFreq) * 0.4;
  const orgSample = clamp(orgLow + orgHigh, -1, 1);

  const cryLow = noise.crystal(v.x * baseFreq, v.y * baseFreq, v.z * baseFreq);
  const cryHigh = noise.detail(v.x * detailFreq + 17.3, v.y * detailFreq + 17.3, v.z * detailFreq + 17.3) * 0.4;
  const crySample = clamp(cryLow + cryHigh, -1, 1);

  return {
    organic: Math.max(0, orgSample) * profile.richnessMultiplier * profile.categoryBias.organic,
    crystal: Math.max(0, crySample) * profile.richnessMultiplier * profile.categoryBias.crystal,
  };
}

// ==================== TERRAIN COLOR ====================

function biomeOverride(
  noise: PlanetNoise,
  v: Vec3,
  lat: number,
  palette: Palette,
): Palette {
  const tempFreq = 1.8;
  const tempN = noise.temperature(v.x * tempFreq, v.y * tempFreq, v.z * tempFreq);
  const latNorm = 1 - Math.abs(lat) / 90;
  const temp = clamp(latNorm * 0.65 + tempN * 0.35, 0, 1);
  const humFreq = 2.5;
  const humN = noise.humidity(v.x * humFreq, v.y * humFreq, v.z * humFreq);
  const hum = clamp(humN * 0.5 + 0.5, 0, 1);

  const top = lerp3(BIOME_CORNERS.coldDry, BIOME_CORNERS.coldWet, hum);
  const bot = lerp3(BIOME_CORNERS.hotDry, BIOME_CORNERS.hotWet, hum);
  const bioLow = lerp3(top, bot, temp);
  // Mid is a bit darker variant of low for stepped depth.
  const bioMid: [number, number, number] = [bioLow[0] * 0.92, bioLow[1] * 0.92, bioLow[2] * 0.92];

  return { ...palette, low: bioLow, mid: bioMid };
}

function applyPolarCap(rgb: [number, number, number], lat: number, planetType: string, band: number): [number, number, number] {
  if (band === 0) return rgb;
  const hasIce = planetType === 'terran' || planetType === 'ice';
  if (!hasIce) return rgb;
  const absLat = Math.abs(lat);
  const capStart = planetType === 'ice' ? 55 : 72;
  const capFull  = planetType === 'ice' ? 75 : 85;
  if (absLat <= capStart) return rgb;
  const cb = smoothstep(capStart, capFull, absLat);
  return [
    rgb[0] + (SNOW[0] - rgb[0]) * cb,
    rgb[1] + (SNOW[1] - rgb[1]) * cb,
    rgb[2] + (SNOW[2] - rgb[2]) * cb,
  ];
}

function computeTerrainColors(
  cells: HexCell[],
  noise: PlanetNoise,
  profile: PlanetProfileWire,
  planetType: string,
  elevations: Float32Array,
  bands: Uint8Array,
): Float32Array {
  const out = new Float32Array(cells.length * 3);
  const palette = getPalette(planetType);
  const stops = getStops(planetType);
  const isTerran = planetType === 'terran';

  for (let i = 0; i < cells.length; i++) {
    const c = cells[i];
    const elev = elevations[i];
    const band = bands[i];
    const pal = isTerran && band > 0 ? biomeOverride(noise, c.center, c.lat, palette) : palette;
    let rgb = elevationToColour(elev, pal, stops);
    rgb = applyPolarCap(rgb, c.lat, planetType, band);
    out[i * 3]     = rgb[0] / 255;
    out[i * 3 + 1] = rgb[1] / 255;
    out[i * 3 + 2] = rgb[2] / 255;
  }
  return out;
}

// ==================== RESOURCE COLOR ====================

interface ResourceState {
  organicBaseline: Float32Array;
  crystalBaseline: Float32Array;
  /** Effective = max(0, baseline + delta). Recomputed on every state change. */
  organicEffective: Float32Array;
  crystalEffective: Float32Array;
  /**
   * Signed per-hex displacement from baseline.
   * Negative = mined; positive = enriched by Phase 5 migration.
   */
  deltaByHex: Map<number, { organic: number; crystal: number }>;
  scannerHexIds: Set<number>;
}

function recomputeResourceEffective(state: ResourceState) {
  for (let i = 0; i < state.organicBaseline.length; i++) {
    const d = state.deltaByHex.get(i);
    state.organicEffective[i] = Math.max(0, state.organicBaseline[i] + (d?.organic ?? 0));
    state.crystalEffective[i] = Math.max(0, state.crystalBaseline[i] + (d?.crystal ?? 0));
  }
}

function computeResourceColors(
  cells: HexCell[],
  state: ResourceState,
): Float32Array {
  const out = new Float32Array(cells.length * 3);
  const orbital = PLANET_BALANCE.ORBITAL_SCAN_NOISE; // 0.3
  for (let i = 0; i < cells.length; i++) {
    let organic = state.organicEffective[i];
    let crystal = state.crystalEffective[i];

    // Outside scanner coverage: orbital-noise degradation (deterministic per hex).
    if (!state.scannerHexIds.has(i)) {
      const c = cells[i].center;
      // Cheap deterministic per-hex jitter: hash centroid coords into [-1, 1].
      const h = Math.sin(c.x * 12.9898 + c.y * 78.233 + c.z * 37.719) * 43758.5453;
      const jitter = (h - Math.floor(h)) * 2 - 1;
      organic = Math.max(0, organic * (1 + jitter * orbital));
      crystal = Math.max(0, crystal * (1 + jitter * orbital * 0.85));
    }

    const intensity = Math.min(1, organic + crystal);
    if (intensity < 0.01) {
      // Dim background — slightly bluish so it doesn't go pure black on top of the planet.
      out[i * 3]     = 0.03;
      out[i * 3 + 1] = 0.04;
      out[i * 3 + 2] = 0.06;
      continue;
    }
    const greenLean = organic / (organic + crystal + 1e-6);
    const bright = Math.pow(intensity, 0.6);
    out[i * 3]     = bright * (1 - greenLean) * 0.24;
    out[i * 3 + 1] = bright * (greenLean * 0.80 + (1 - greenLean) * 0.32);
    out[i * 3 + 2] = bright * (1 - greenLean) * 0.88;
  }
  return out;
}

// ==================== GEOMETRY BUILDER ====================

interface BuiltGeometry {
  geometry: THREE.BufferGeometry;
  /** Per-cell start index (into positions/colors arrays) for fast color updates. */
  cellVertexStart: Uint32Array;
  /** Per-cell vertex count (15 for hex, 13 for pent — but we use flat unique verts so it's 9·N per cell). */
  cellVertexCount: Uint8Array;
  /** Triangle index → cell id (for raycast resolution). */
  faceToCell: Uint16Array;
  totalVertices: number;
  totalTriangles: number;
}

/**
 * For each cell, emit:
 *   - N top triangles (centroid + adjacent corners) — N is 5 or 6
 *   - N side quads (= 2N triangles) connecting top corners to base corners
 * Vertices are NOT shared between triangles — gives flat shading via per-triangle uniform color.
 * Per cell: 3N triangles × 3 verts = 9N verts (54 for hex, 45 for pent).
 */
function buildGeometry(
  cells: HexCell[],
  bands: Uint8Array,
): BuiltGeometry {
  // Compute total counts.
  let totalTris = 0;
  for (const c of cells) totalTris += 3 * c.corners.length;
  const totalVerts = totalTris * 3;

  const positions = new Float32Array(totalVerts * 3);
  const colors = new Float32Array(totalVerts * 3); // initial dummy, recomputed via setLayer
  const faceToCell = new Uint16Array(totalTris);
  const cellVertexStart = new Uint32Array(cells.length);
  const cellVertexCount = new Uint8Array(cells.length);

  let vIdx = 0;
  let tIdx = 0;

  for (let ci = 0; ci < cells.length; ci++) {
    const c = cells[ci];
    const N = c.corners.length;
    const band = bands[ci];
    const r_top = 1 + BAND_HEIGHT[band];
    const r_bot = 1; // bottom always at unit sphere

    cellVertexStart[ci] = vIdx;

    const topC = hexToThree(c.center).multiplyScalar(r_top);
    const topCorn = c.corners.map((p) => hexToThree(p).multiplyScalar(r_top));
    const botCorn = c.corners.map((p) => hexToThree(p).multiplyScalar(r_bot));

    // ---- Top face: fan from centroid ----
    for (let i = 0; i < N; i++) {
      const a = topC;
      const b = topCorn[i];
      const cc = topCorn[(i + 1) % N];
      // tri (a, b, cc) — write 3 verts
      positions[vIdx * 3 + 0] = a.x; positions[vIdx * 3 + 1] = a.y; positions[vIdx * 3 + 2] = a.z; vIdx++;
      positions[vIdx * 3 + 0] = b.x; positions[vIdx * 3 + 1] = b.y; positions[vIdx * 3 + 2] = b.z; vIdx++;
      positions[vIdx * 3 + 0] = cc.x; positions[vIdx * 3 + 1] = cc.y; positions[vIdx * 3 + 2] = cc.z; vIdx++;
      faceToCell[tIdx++] = ci;
    }

    // ---- Side walls: 2 triangles per edge ----
    for (let i = 0; i < N; i++) {
      const tA = topCorn[i];
      const tB = topCorn[(i + 1) % N];
      const bA = botCorn[i];
      const bB = botCorn[(i + 1) % N];
      // Quad (tA, bA, bB, tB) split as (tA, bA, bB) and (tA, bB, tB)
      positions[vIdx * 3 + 0] = tA.x; positions[vIdx * 3 + 1] = tA.y; positions[vIdx * 3 + 2] = tA.z; vIdx++;
      positions[vIdx * 3 + 0] = bA.x; positions[vIdx * 3 + 1] = bA.y; positions[vIdx * 3 + 2] = bA.z; vIdx++;
      positions[vIdx * 3 + 0] = bB.x; positions[vIdx * 3 + 1] = bB.y; positions[vIdx * 3 + 2] = bB.z; vIdx++;
      faceToCell[tIdx++] = ci;

      positions[vIdx * 3 + 0] = tA.x; positions[vIdx * 3 + 1] = tA.y; positions[vIdx * 3 + 2] = tA.z; vIdx++;
      positions[vIdx * 3 + 0] = bB.x; positions[vIdx * 3 + 1] = bB.y; positions[vIdx * 3 + 2] = bB.z; vIdx++;
      positions[vIdx * 3 + 0] = tB.x; positions[vIdx * 3 + 1] = tB.y; positions[vIdx * 3 + 2] = tB.z; vIdx++;
      faceToCell[tIdx++] = ci;
    }

    cellVertexCount[ci] = 9 * N; // 3 verts × (N top tris + 2N side tris)
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.computeBoundingSphere();

  return {
    geometry,
    cellVertexStart,
    cellVertexCount,
    faceToCell,
    totalVertices: totalVerts,
    totalTriangles: totalTris,
  };
}

/** Write a per-cell color into all of that cell's vertex slots. */
function applyCellColors(
  colorAttr: THREE.BufferAttribute,
  cellVertexStart: Uint32Array,
  cellVertexCount: Uint8Array,
  colorsPerCell: Float32Array,
) {
  const arr = colorAttr.array as Float32Array;
  for (let ci = 0; ci < cellVertexStart.length; ci++) {
    const r = colorsPerCell[ci * 3];
    const g = colorsPerCell[ci * 3 + 1];
    const b = colorsPerCell[ci * 3 + 2];
    const start = cellVertexStart[ci];
    const end = start + cellVertexCount[ci];
    for (let v = start; v < end; v++) {
      arr[v * 3]     = r;
      arr[v * 3 + 1] = g;
      arr[v * 3 + 2] = b;
    }
  }
  colorAttr.needsUpdate = true;
}

// ==================== HEX LOOKUP FOR SCANNER ZONES ====================

/** Build the set of hex ids whose centroid lies within any of the given scanner zones. */
function scannerHexesFromZones(
  cells: HexCell[],
  zones: ScannerZoneWire[],
  planetRadiusKm: number,
): Set<number> {
  const out = new Set<number>();
  if (zones.length === 0) return out;
  for (const z of zones) {
    const angR = z.radiusKm / planetRadiusKm;
    const cosThresh = Math.cos(angR);
    const phi = (z.lat * Math.PI) / 180;
    const lam = (z.lon * Math.PI) / 180;
    const cp = Math.cos(phi);
    const zx = cp * Math.cos(lam);
    const zy = cp * Math.sin(lam);
    const zz = Math.sin(phi);
    for (let i = 0; i < cells.length; i++) {
      const c = cells[i].center;
      const d = c.x * zx + c.y * zy + c.z * zz;
      if (d >= cosThresh) out.add(i);
    }
  }
  return out;
}

// ==================== PUBLIC FACTORY ====================

export interface BuildHexMeshOptions {
  /** Earth-radius scaled by sqrt(size_multiplier). Used for scanner radius → angular conversion. */
  planetRadiusKm: number;
  /** Hexsphere frequency for this planet (6 | 8 | 10). Required. */
  meshFrequency: HexFrequency;
  /** Initial layer. Default: 'terrain'. */
  initialLayer?: 'terrain' | 'resource';
  /** Initial per-hex delta state (Phase 3+ authoritative). */
  initialHexState?: HexStateWire[];
  /** Initial richness depletion diffs (legacy; for Phase 1/2 compat). */
  initialDiffs?: RichnessDiffWire[];
  /** Initial scanner zones. */
  initialScannerZones?: ScannerZoneWire[];
}

export function buildPlanetHexMesh(
  profile: PlanetProfileWire,
  planetType: string,
  opts: BuildHexMeshOptions,
): BuiltPlanetHexMesh {
  const { cells } = getHexSphere(opts.meshFrequency);
  const noise = buildNoise(profile.profileSeed);

  // ---- Per-cell elevation + band ----
  const elevations = new Float32Array(cells.length);
  const bands = new Uint8Array(cells.length);
  const stops = getStops(planetType);
  for (let i = 0; i < cells.length; i++) {
    const e = sampleElevation(noise, cells[i].center, profile.clusterDensity, planetType);
    elevations[i] = e;
    bands[i] = elevationBand(e, stops);
  }

  // ---- Per-cell resource baselines ----
  const organicBaseline = new Float32Array(cells.length);
  const crystalBaseline = new Float32Array(cells.length);
  for (let i = 0; i < cells.length; i++) {
    const r = sampleResourceField(noise, cells[i].center, profile);
    organicBaseline[i] = r.organic;
    crystalBaseline[i] = r.crystal;
  }

  // ---- Geometry ----
  const built = buildGeometry(cells, bands);
  const material = new THREE.MeshStandardMaterial({
    vertexColors: true,
    flatShading: true,
    roughness: 0.85,
    metalness: 0.0,
  });
  const mesh = new THREE.Mesh(built.geometry, material);
  mesh.userData.faceToCell = built.faceToCell;
  mesh.userData.cellCount = cells.length;

  // ---- Selection highlight ----
  // The primary selection cue is gold top-face vertex colors (applied via
  // paintSelectionOnTop, defined alongside renderCurrentLayer below). This
  // group adds a crisp gold perimeter outline as a secondary signal that
  // reinforces the hex boundary regardless of camera angle.
  const highlightGroup = new THREE.Group();
  mesh.add(highlightGroup);
  let currentHighlight: THREE.Line | null = null;
  const disposeHighlight = () => {
    if (currentHighlight) {
      highlightGroup.remove(currentHighlight);
      currentHighlight.geometry.dispose();
      (currentHighlight.material as THREE.Material).dispose();
      currentHighlight = null;
    }
  };
  const buildHighlight = (hexId: number) => {
    const cell = cells[hexId];
    const band = bands[hexId];
    const r_top = 1 + BAND_HEIGHT[band] + 0.004; // sit just above the gold top
    const ringPts = cell.corners.map((c) => hexToThree(c).multiplyScalar(r_top));
    ringPts.push(ringPts[0].clone());

    const ringGeom = new THREE.BufferGeometry().setFromPoints(ringPts);
    const ringMat = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.85,
      depthTest: false,
    });
    currentHighlight = new THREE.Line(ringGeom, ringMat);
    currentHighlight.renderOrder = 2;
    highlightGroup.add(currentHighlight);
  };

  // ---- Precomputed terrain colors ----
  const terrainColors = computeTerrainColors(cells, noise, profile, planetType, elevations, bands);

  // ---- Resource state ----
  const resourceState: ResourceState = {
    organicBaseline,
    crystalBaseline,
    organicEffective: new Float32Array(cells.length),
    crystalEffective: new Float32Array(cells.length),
    deltaByHex: new Map(),
    scannerHexIds: new Set(),
  };
  // Phase 5+: signed delta. Negative = mined, positive = enriched by migration.
  // Effective = max(0, baseline + delta).
  if (opts.initialHexState) {
    for (const r of opts.initialHexState) {
      if (r.hex_id < 0 || r.hex_id >= cells.length) continue;
      resourceState.deltaByHex.set(r.hex_id, {
        organic: r.delta_organic,
        crystal: r.delta_crystal,
      });
    }
  } else if (opts.initialDiffs) {
    // Legacy fallback: best-effort (cell_lat, cell_lon) → nearest hex.
    // The legacy wire stored positive depletion magnitudes; convert to
    // negative signed delta to match the new model.
    for (const d of opts.initialDiffs) {
      const lat = d.cell_lat / 111;
      const lon = d.cell_lon / 111;
      const phi = (lat * Math.PI) / 180, lam = (lon * Math.PI) / 180;
      const cp = Math.cos(phi);
      const px = cp * Math.cos(lam), py = cp * Math.sin(lam), pz = Math.sin(phi);
      let best = 0, bestDot = -2;
      for (let i = 0; i < cells.length; i++) {
        const c = cells[i].center;
        const dd = px * c.x + py * c.y + pz * c.z;
        if (dd > bestDot) { bestDot = dd; best = i; }
      }
      const prev = resourceState.deltaByHex.get(best) ?? { organic: 0, crystal: 0 };
      prev.organic -= d.depletion_organic;
      prev.crystal -= d.depletion_crystal;
      resourceState.deltaByHex.set(best, prev);
    }
  }
  if (opts.initialScannerZones) {
    resourceState.scannerHexIds = scannerHexesFromZones(cells, opts.initialScannerZones, opts.planetRadiusKm);
  }
  recomputeResourceEffective(resourceState);

  // ---- Apply initial layer ----
  let currentLayer: 'terrain' | 'resource' = opts.initialLayer ?? 'terrain';
  const colorAttr = built.geometry.getAttribute('color') as THREE.BufferAttribute;

  /**
   * Paint a single cell's TOP-face triangles (the fan from centroid →
   * adjacent corners) with the gold selection color, leaving its side walls
   * alone so the prism still reads as an extruded hex.
   *
   * Top triangles live at the START of each cell's vertex range — buildGeometry
   * emits them first: N top triangles × 3 verts = first 3·N verts of the cell.
   */
  let selectedHexId: number | null = null;
  // Gold #ffe060 — brighter than the line so the hex pops against the muted
  // terrain palette regardless of zoom level (WebGL caps lineWidth at 1px).
  const SEL_R = 1.0, SEL_G = 0.88, SEL_B = 0.38;
  const paintSelectionOnTop = () => {
    if (selectedHexId == null) return;
    const arr = colorAttr.array as Float32Array;
    const cell = cells[selectedHexId];
    const start = built.cellVertexStart[selectedHexId];
    const topVerts = 3 * cell.corners.length; // N top tris × 3 verts
    for (let v = 0; v < topVerts; v++) {
      const off = (start + v) * 3;
      arr[off]     = SEL_R;
      arr[off + 1] = SEL_G;
      arr[off + 2] = SEL_B;
    }
    colorAttr.needsUpdate = true;
  };

  const renderCurrentLayer = () => {
    if (currentLayer === 'terrain') {
      applyCellColors(colorAttr, built.cellVertexStart, built.cellVertexCount, terrainColors);
    } else {
      const rc = computeResourceColors(cells, resourceState);
      applyCellColors(colorAttr, built.cellVertexStart, built.cellVertexCount, rc);
    }
    // Restore selection tint on top of the freshly-painted base colors.
    paintSelectionOnTop();
  };
  renderCurrentLayer();

  return {
    mesh,
    faceToCell: built.faceToCell,
    cellCount: cells.length,
    cellBand: bands,
    setLayer(layer) {
      if (layer === currentLayer) return;
      currentLayer = layer;
      renderCurrentLayer();
    },
    setScannerZones(zones) {
      resourceState.scannerHexIds = scannerHexesFromZones(cells, zones, opts.planetRadiusKm);
      if (currentLayer === 'resource') renderCurrentLayer();
    },
    setSelectedHex(hexId) {
      // Validate + update local state first; renderCurrentLayer() will pick it up.
      const valid = hexId != null && hexId >= 0 && hexId < cells.length ? hexId : null;
      const changed = valid !== selectedHexId;
      selectedHexId = valid;

      // Rebuild the line/fill outline (cheap — N segments).
      disposeHighlight();
      if (selectedHexId != null) buildHighlight(selectedHexId);

      // Repaint all cells to wipe the previous selection's gold tint, then
      // apply gold on the new selection (done inside renderCurrentLayer).
      if (changed) renderCurrentLayer();
    },
    setHexState(rows) {
      // Phase 5+: merge by hex_id. The wire delivers ABSOLUTE current delta
      // per hex; zero values from cleanup remove the row from local state so
      // the renderer paints them back at baseline.
      const eps = 1e-6;
      for (const r of rows) {
        if (r.hex_id < 0 || r.hex_id >= cells.length) continue;
        if (Math.abs(r.delta_organic) < eps && Math.abs(r.delta_crystal) < eps) {
          resourceState.deltaByHex.delete(r.hex_id);
        } else {
          resourceState.deltaByHex.set(r.hex_id, {
            organic: r.delta_organic,
            crystal: r.delta_crystal,
          });
        }
      }
      recomputeResourceEffective(resourceState);
      if (currentLayer === 'resource') renderCurrentLayer();
    },
    setRichnessDiffs(diffs) {
      resourceState.deltaByHex.clear();
      for (const d of diffs) {
        const lat = d.cell_lat / 111;
        const lon = d.cell_lon / 111;
        const phi = (lat * Math.PI) / 180, lam = (lon * Math.PI) / 180;
        const cp = Math.cos(phi);
        const px = cp * Math.cos(lam), py = cp * Math.sin(lam), pz = Math.sin(phi);
        let best = 0, bestDot = -2;
        for (let i = 0; i < cells.length; i++) {
          const c = cells[i].center;
          const dd = px * c.x + py * c.y + pz * c.z;
          if (dd > bestDot) { bestDot = dd; best = i; }
        }
        const prev = resourceState.deltaByHex.get(best) ?? { organic: 0, crystal: 0 };
        prev.organic -= d.depletion_organic;
        prev.crystal -= d.depletion_crystal;
        resourceState.deltaByHex.set(best, prev);
      }
      recomputeResourceEffective(resourceState);
      if (currentLayer === 'resource') renderCurrentLayer();
    },
    getCell(id) { return cells[id]; },
    getEffectiveRichness(id) {
      return {
        organic: resourceState.organicEffective[id],
        crystal: resourceState.crystalEffective[id],
      };
    },
    dispose() {
      disposeHighlight();
      built.geometry.dispose();
      material.dispose();
    },
  };
}
